import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'

import { getPortalGate, PORTAL_GATE_PATH } from '@/access/portal-access'
import { ImpersonationBanner } from '@/components/impersonation-banner'
import { MobileNav } from '@/components/mobile-nav'
import { Sidebar } from '@/components/sidebar'
import { isAdmin } from '@/hooks/use-is-admin'
import { store } from '@/store'

const PARTNER_ROUTES = new Set([
  '/dashboard',
  '/commissions',
  '/reports',
  '/coupons',
  '/payouts',
  '/settings',
])

export const Route = createFileRoute('/_private')({
  component: PrivateLayout,
  beforeLoad: async ({ location }) => {
    const state = store.getState()
    if (!state.isInitialized) {
      await state.initialize()
    }

    if (!store.getState().isAuthorized) {
      throw redirect({
        to: '/login',
        replace: true,
      })
    }

    // Admins pass the portal gate itself (`getPortalGate` mirrors the backend's
    // admin bypass): they reach the admin area regardless of their own
    // can_access_portal / terms_accepted_at, and must never be shown the
    // partner-facing T&C wizard or the beta-locked screen.
    //
    // Impersonation is exempt here. While an admin is "viewing as" a partner,
    // store.partner is the TARGET profile — so isAdmin() is false and the target's
    // gate may not be 'allow'. Without this exemption the admin would be bounced to
    // the partner-facing T&C wizard / beta-locked screen, and on /accept-terms the
    // still-attached impersonation header would let them accept the partner's terms
    // on their behalf. The backend is the real enforcement (verified admin + 403 on
    // impersonated writes); this keeps the FE from ever routing into that gate.
    //
    // A non-admin who hand-writes the impersonation key in sessionStorage can skip
    // this UX gate and render the portal shell, but that is cosmetic only: every
    // data call stays BE-gated and the forged X-Impersonate header is honored solely
    // for verified admins (gto-wizard#2449). We deliberately do NOT tighten this to
    // `isAdmin() && impersonating` — during a real impersonation store.partner IS the
    // (non-admin) target, so that check is always false and would reinstate the exact
    // bounce this exemption removes. The authorization boundary is the backend.
    const impersonating = store.getState().impersonation !== null
    const gate = getPortalGate(store.getState().partner)
    if (!impersonating && gate.kind !== 'allow') {
      throw redirect({
        to: PORTAL_GATE_PATH(gate),
        replace: true,
      })
    }

    if (isAdmin() && PARTNER_ROUTES.has(location.pathname)) {
      throw redirect({
        to: '/admin/partners',
        replace: true,
      })
    }
  },
})

function PrivateLayout() {
  return (
    <div className="flex min-h-[100dvh] flex-row md:flex-col">
      <Sidebar />
      <MobileNav />
      <main className="flex min-h-0 min-w-0 flex-1 flex-col p-2">
        <ImpersonationBanner />
        <div
          // overflow-clip, NOT overflow-hidden: hidden makes this div a scroll
          // container, which becomes the scrollport for any position:sticky
          // descendant (e.g. the partner-detail rail) — and since the div
          // itself never scrolls, sticky silently never engages. clip gives
          // the same rounded-corner clipping without creating a scrollport.
          className="flex flex-1 flex-col overflow-clip rounded-lg bg-gradient-paper1 p-8 md:p-6 sm:p-4"
        >
          <Outlet />
        </div>
      </main>
    </div>
  )
}
